import { useQuery, type QueryKey } from '@tanstack/react-query';

import { api } from 'lib/request/api';
import type {
  InventoryWorkspaceUnitRow,
  InventoryWorkspaceUnitsResponse,
} from 'types/api-responses';

export const INVENTORY_UNIT_VERDICT_KEY = 'inventory-unit-verdict';

export const inventoryUnitVerdictKey = (
  workspaceId: number | null,
  unitId: number | null
): QueryKey => [INVENTORY_UNIT_VERDICT_KEY, workspaceId, unitId];

/** The selected unit's own per-unit verdict on an Inventory Workspace. */
export interface InventoryUnitVerdict {
  /** The unit's verification item, the one the verdict events write to. */
  verificationId: number | null;
  /** true = correct, false = incorrect, null = unverified. */
  status: boolean | null;
}

const NO_VERDICT: InventoryUnitVerdict = { verificationId: null, status: null };

/**
 * The selected unit's verdict on an Inventory Workspace, read from the
 * workspace's own unit membership (`GET /inventory_workspaces/:id/units`) — the
 * inventory counterpart of the Type Curve Workspace's header units read. The
 * list is searched by name and the row matched on the development-area id.
 *
 * @param {number | null} workspaceId - The Inventory Workspace, or null off it.
 * @param {number | null} unitId - The selected unit's development-area id.
 * @param {string | null} unitName - The selected unit's name (the search term).
 * @return {object} The verdict, and whether it is still loading.
 */
export function useInventoryUnitVerdict(
  workspaceId: number | null,
  unitId: number | null,
  unitName: string | null
) {
  const { data, isFetching } = useQuery<InventoryUnitVerdict>({
    queryKey: inventoryUnitVerdictKey(workspaceId, unitId),
    queryFn: async ({ signal }) => {
      const response = await api
        .get(`inventory_workspaces/${workspaceId}/units`, {
          searchParams: { search: unitName ?? '', pageSize: 200 },
          signal,
        })
        .json<InventoryWorkspaceUnitsResponse>();
      const row = response.units.find(
        (u: InventoryWorkspaceUnitRow) => u.unitId === unitId
      );
      return row
        ? {
            verificationId: row.verification.id,
            status: row.verification.status,
          }
        : NO_VERDICT;
    },
    enabled: workspaceId !== null && unitId !== null && !!unitName,
  });

  return { verdict: data ?? NO_VERDICT, loading: isFetching };
}
